<template>
  <Notifications />
  <div v-if="shouldRender">
    <HeaderBar @onPatientNameClick="isMetadataPanelOpen = true" />
    <router-view />
  </div>
  <LoadingOverlay v-if="isClaimLoading" />
  <GlobalErrorOverlay />
  <BaseModalComponent
    :title="baseModalTitle"
    :description="baseModalDescription"
    :is-open="isBaseModalOpen"
    :header-icon="'fa-info-circle'"
  />
  <ReleaseNotesDialogComponent />
  <StudyMetadataPanel v-model:isOpen="isMetadataPanelOpen" />
</template>

<script setup lang="ts">
defineOptions({ name: 'BaseApp' });

import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { GlobalErrorOverlay, LoadingOverlay } from './common/ui';
import { Notifications, HeaderBar } from './core/components';
import BaseModalComponent from './core/components/modal/base-modal.component.vue';
import ReleaseNotesDialogComponent from './modules/release-notes/feature/release-notes-dialog.component.vue';
import { useGetBrowserInfo } from './core/hooks/useCheckBrowser';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useGlobalErrorOverlay, useReadOnly } from './common/utils';
import { NotificationTypes } from './common/types';
import StudyMetadataPanel from './common/ui/components/study-metadata-panel/study-metadata-panel.component.vue';

const store = useStore();
const route = useRoute();
const intervalId = ref<ReturnType<typeof setInterval>>();
const CLAIM_INTERVAL_TIME = 5 * 60 * 1000;

const isClaimSuccess = ref<boolean>(false);
const isClaimLoading = ref<boolean>(false);

const isMetadataPanelOpen = ref<boolean>(false);

onBeforeMount(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

const { t } = useI18n();
const { isReadOnly } = useReadOnly();

const { watchStudyStatePreparing } = useGlobalErrorOverlay();

const isStudyStatePreparing = computed(() => store.getters['scoreSet/isStudyStatePreparing']);

watchStudyStatePreparing(isStudyStatePreparing, t);

const { isBrowserValid, isResolutionValid, isBrowserCheckActive } = useGetBrowserInfo();

const isBaseModalOpen = computed<boolean>(() =>
  isBrowserCheckActive.value && !hasDisableChecks.value
    ? !isBrowserValid.value || !isResolutionValid.value
    : false
);

const hasDisableChecks = computed(() => route.query?.disable_checks === 'True');

const shouldRender = computed(() => !isStudyStatePreparing.value && isClaimSuccess.value);

const baseModalTitle = computed(() =>
  !isBrowserValid.value ? t('browserWarning.versionTitle') : t('browserWarning.resolutionTitle')
);

const baseModalDescription = computed(() =>
  !isBrowserValid.value
    ? t('browserWarning.versionDescription')
    : t('browserWarning.resolutionDesctiption')
);

const studyID = computed(() => route.params.id);

const handleBeforeUnload = () => {
  if (!studyID.value) return;

  store.dispatch('epoch/deleteClaim', studyID.value);
};

const callClaim = async (isFirstClaim: boolean) => {
  if (!studyID.value) return;

  isClaimSuccess.value = await store.dispatch('epoch/claimStudy', {
    sleepStudyID: studyID.value,
    isFirstClaim
  });
};

watch(
  studyID,
  async (paramID) => {
    if (paramID) {
      isClaimLoading.value = true;
      try {
        await callClaim(true);
        intervalId.value = setInterval(async () => {
          await callClaim(false);
        }, CLAIM_INTERVAL_TIME);
        await store.dispatch('epoch/getPresetSettings');
      } finally {
        isClaimLoading.value = false;
      }
    }
  },
  { immediate: true }
);

watch(
  isReadOnly,
  (value) => {
    if (value) {
      store.commit(
        'addNotification',
        {
          i18nTitle: 'warningViewOnlyTitle',
          i18nDescription: 'warningViewOnlyDescription',
          type: NotificationTypes.Warning
        },
        { root: true }
      );
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>
